import React, { useState, useEffect, useCallback } from 'react';
import { groupService, Group, GroupVisibility, User } from '../services/groupService';
import UserAutocomplete from './UserAutocomplete';

interface GroupManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const GroupManagementModal: React.FC<GroupManagementModalProps> = ({ isOpen, onClose }) => {
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [view, setView] = useState<'list' | 'create' | 'detail'>('list');
  const [selectedGroup, setSelectedGroup] = useState<Group | null>(null);

  // Create group form
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupDescription, setNewGroupDescription] = useState('');
  const [newGroupVisibility, setNewGroupVisibility] = useState<GroupVisibility>('public');
  const [creating, setCreating] = useState(false);

  // Add members form
  const [selectedUsers, setSelectedUsers] = useState<User[]>([]);
  const [addingMembers, setAddingMembers] = useState(false);

  const fetchGroups = useCallback(async () => {
    setLoading(true);
    try {
      const data = await groupService.getGroups();
      setGroups(data);
      setError('');
    } catch (err) {
      setError('Failed to load groups');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchGroupDetail = useCallback(async (groupId: string) => {
    try {
      const data = await groupService.getGroup(groupId);
      setSelectedGroup(data);
    } catch (err) {
      setError('Failed to load group details');
      console.error(err);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchGroups();
    }
  }, [isOpen, fetchGroups]);

  const handleCreateGroup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newGroupName.trim()) return;

    setCreating(true);
    try {
      await groupService.createGroup({
        name: newGroupName.trim(),
        description: newGroupDescription.trim() || undefined,
        visibility: newGroupVisibility,
      });
      setNewGroupName('');
      setNewGroupDescription('');
      setNewGroupVisibility('public');
      setView('list');
      await fetchGroups();
    } catch (err: any) {
      setError(err.message || 'Failed to create group');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteGroup = async (groupId: string) => {
    if (!window.confirm('Are you sure you want to delete this group?')) return;

    try {
      await groupService.deleteGroup(groupId);
      if (selectedGroup?.id === groupId) {
        setSelectedGroup(null);
        setView('list');
      }
      await fetchGroups();
    } catch (err: any) {
      setError(err.message || 'Failed to delete group');
    }
  };

  const handleAddMembers = async () => {
    if (!selectedGroup || selectedUsers.length === 0) return;

    setAddingMembers(true);
    try {
      await groupService.addMembers(selectedGroup.id, selectedUsers.map(u => u.id));
      setSelectedUsers([]);
      await fetchGroupDetail(selectedGroup.id);
      await fetchGroups();
    } catch (err: any) {
      setError(err.message || 'Failed to add members');
    } finally {
      setAddingMembers(false);
    }
  };

  const handleRemoveMember = async (userId: string) => {
    if (!selectedGroup) return;

    try {
      await groupService.removeMember(selectedGroup.id, userId);
      await fetchGroupDetail(selectedGroup.id);
      await fetchGroups();
    } catch (err: any) {
      setError(err.message || 'Failed to remove member');
    }
  };

  const openGroupDetail = async (group: Group) => {
    setSelectedGroup(group);
    await fetchGroupDetail(group.id);
    setView('detail');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Backdrop */}
        <div className="fixed inset-0 transition-opacity bg-gray-500 dark:bg-gray-900 bg-opacity-75 dark:bg-opacity-75" onClick={onClose} />

        {/* Modal */}
        <div className="inline-block overflow-hidden text-left align-bottom transition-all transform bg-white dark:bg-gray-800 rounded-lg shadow-xl sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <div className="flex items-center gap-3">
              {view !== 'list' && (
                <button
                  onClick={() => { setView('list'); setSelectedGroup(null); }}
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
              )}
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {view === 'list' && 'Manage Groups'}
                {view === 'create' && 'Create New Group'}
                {view === 'detail' && selectedGroup?.name}
              </h3>
            </div>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Error message */}
          {error && (
            <div className="mx-6 mt-4 px-4 py-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-600 dark:text-red-400 text-sm">
              {error}
              <button onClick={() => setError('')} className="ml-2 underline">Dismiss</button>
            </div>
          )}

          {/* Content */}
          <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
            {/* List View */}
            {view === 'list' && (
              <>
                {loading ? (
                  <div className="flex justify-center py-8">
                    <svg className="animate-spin h-8 w-8 text-blue-500" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                  </div>
                ) : groups.length === 0 ? (
                  <div className="text-center py-8">
                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    <h4 className="mt-3 text-lg font-medium text-gray-900 dark:text-white">No groups yet</h4>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Create a group to share customers with your team.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {groups.map((group) => (
                      <div
                        key={group.id}
                        className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-blue-300 dark:hover:border-blue-700 transition-colors cursor-pointer"
                        onClick={() => openGroupDetail(group)}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <h4 className="font-medium text-gray-900 dark:text-white">{group.name}</h4>
                              {group.visibility === 'private' && (
                                <span className="px-2 py-0.5 text-xs rounded-full bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
                                  Private
                                </span>
                              )}
                            </div>
                            {group.description && (
                              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{group.description}</p>
                            )}
                            <div className="flex items-center gap-4 mt-2 text-xs text-gray-500 dark:text-gray-400">
                              <span>{group.memberCount} member{group.memberCount !== 1 ? 's' : ''}</span>
                              <span>Created by {group.createdBy.firstName} {group.createdBy.lastName}</span>
                            </div>
                          </div>
                          {group.isCreator && (
                            <button
                              onClick={(e) => { e.stopPropagation(); handleDeleteGroup(group.id); }}
                              className="p-1 text-gray-400 hover:text-red-500 dark:hover:text-red-400"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* Create View */}
            {view === 'create' && (
              <form onSubmit={handleCreateGroup} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Group Name *
                  </label>
                  <input
                    type="text"
                    value={newGroupName}
                    onChange={(e) => setNewGroupName(e.target.value)}
                    placeholder="e.g., Marketing Team"
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Description (optional)
                  </label>
                  <textarea
                    value={newGroupDescription}
                    onChange={(e) => setNewGroupDescription(e.target.value)}
                    placeholder="Describe the purpose of this group..."
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Visibility
                  </label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setNewGroupVisibility('public')}
                      className={`flex-1 px-3 py-2 text-sm rounded-lg border text-left ${
                        newGroupVisibility === 'public'
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                          : 'border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                    >
                      <div className="font-medium">Public</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Everyone in your company can see this group</div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setNewGroupVisibility('private')}
                      className={`flex-1 px-3 py-2 text-sm rounded-lg border text-left ${
                        newGroupVisibility === 'private'
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                          : 'border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                    >
                      <div className="font-medium">Private</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Only you can see and use this group</div>
                    </button>
                  </div>
                </div>
              </form>
            )}

            {/* Detail View */}
            {view === 'detail' && selectedGroup && (
              <div className="space-y-6">
                {/* Group info */}
                {selectedGroup.description && (
                  <p className="text-gray-600 dark:text-gray-400">{selectedGroup.description}</p>
                )}

                {/* Add members section */}
                <div>
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Add Members</h4>
                  <UserAutocomplete
                    selectedUsers={selectedUsers}
                    onSelect={setSelectedUsers}
                    excludeUserIds={selectedGroup.members?.map(m => m.user.id) || []}
                    placeholder="Search for team members..."
                  />
                  {selectedUsers.length > 0 && (
                    <button
                      onClick={handleAddMembers}
                      disabled={addingMembers}
                      className="mt-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                    >
                      {addingMembers ? 'Adding...' : `Add ${selectedUsers.length} Member${selectedUsers.length > 1 ? 's' : ''}`}
                    </button>
                  )}
                </div>

                {/* Members list */}
                <div>
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Members ({selectedGroup.members?.length || 0})
                  </h4>
                  {selectedGroup.members && selectedGroup.members.length > 0 ? (
                    <div className="space-y-2">
                      {selectedGroup.members.map((member) => (
                        <div
                          key={member.id}
                          className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                              <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
                                {member.user.firstName[0]}{member.user.lastName[0]}
                              </span>
                            </div>
                            <div>
                              <div className="font-medium text-gray-900 dark:text-white text-sm">
                                {member.user.firstName} {member.user.lastName}
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                {member.user.email}
                              </div>
                            </div>
                          </div>
                          {(selectedGroup.canManageMembers || selectedGroup.isCreator) && (
                            <button
                              onClick={() => handleRemoveMember(member.user.id)}
                              className="text-gray-400 hover:text-red-500 dark:hover:text-red-400"
                              title="Remove member"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                      No members yet. Add some team members above.
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-between">
            {view === 'list' && (
              <>
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                >
                  Close
                </button>
                <button
                  onClick={() => setView('create')}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Create Group
                </button>
              </>
            )}
            {view === 'create' && (
              <>
                <button
                  onClick={() => setView('list')}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateGroup as any}
                  disabled={creating || !newGroupName.trim()}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {creating ? 'Creating...' : 'Create Group'}
                </button>
              </>
            )}
            {view === 'detail' && (
              <button
                onClick={() => { setView('list'); setSelectedGroup(null); }}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              >
                Back to Groups
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GroupManagementModal;
